// JOBE-0158.2 — <Spark /> SVG sparkline component.
//
// Pinned-shape primitive consumed by five+ downstream W-screen surfaces
// (W1/W2/W5/W6/W7). Don't change the prop names without coordinating with
// JOBE-0159 / 0160 / 0163 / 0164 / 0165 first.
//
// Two modes:
//   - absolute:  y-axis = [min(data), max(data)] (extended to include
//                an optional `target` line)
//   - deviation: y-axis is symmetric around the mean (±max|v-mean|·1.3),
//                with a dashed centerline at the mean
//
// Tone is a consumer-driven semantic (ok|warn|bad|ink); the component
// never infers tone from trend direction. See decisions.md §D11.
//
// Geometry: intrinsic viewBox 140×32 with 2px inset padding. Path is
// smoothed via a Catmull-Rom → cubic-Bezier conversion (tension=0.5,
// closest visual match to the proposal HTML).

import { useId, useRef, useState } from 'react';
import './Spark.css';

const VB_W = 140;
const VB_H = 32;
const PAD = 2;

const MODES = new Set(['absolute', 'deviation']);
const TONES = new Set(['ok', 'warn', 'bad', 'ink']);

function fmtSigned(n) {
  // Explicit + / − (Unicode minus) per spec.
  if (n > 0) return `+${formatNum(n)}`;
  if (n < 0) return `−${formatNum(Math.abs(n))}`;
  return `+${formatNum(0)}`;
}

function formatNum(n) {
  // Defensive: null/undefined/NaN values surface as an em-dash so a
  // partial data point inside an otherwise-valid series doesn't crash
  // the entire panel. Consumers with no data should pass an empty
  // `data` array or omit the value; this guard exists for the
  // partial-data case where some points are real and some are missing.
  if (n == null || Number.isNaN(n)) return '—';
  // Keep one decimal for fractional values, integers stay integer.
  // Consumers can pre-format if they need more control; the spec
  // does not pin precision beyond "looks like the proposal".
  if (Number.isInteger(n)) return String(n);
  const abs = Math.abs(n);
  if (abs >= 100) return n.toFixed(0);
  if (abs >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

// Catmull-Rom → cubic Bezier path. Smoother than naive line-to and
// reads as a single continuous curve at 30px height, which is the
// look the wireframes use.
function smoothPath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const [p] = points;
    return `M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }
  const t = 0.5; // tension
  let d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const c1x = p1.x + ((p2.x - p0.x) / 6) * t * 2;
    const c1y = p1.y + ((p2.y - p0.y) / 6) * t * 2;
    const c2x = p2.x - ((p3.x - p1.x) / 6) * t * 2;
    const c2y = p2.y - ((p3.y - p1.y) / 6) * t * 2;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

export function Spark({
  data,
  mode,
  tone,
  unit = '',
  target,
  ariaLabel,
}) {
  const containerRef = useRef(null);
  const reactId = useId();
  const [hoverIdx, setHoverIdx] = useState(null);

  // -------------------- prop validation (warn, don't throw) --------------------
  let effectiveMode = mode;
  let effectiveTone = tone;
  let effectiveTarget = target;

  // Filter out null/undefined/NaN early so a partial-data series (the
  // common case when a backend metrics endpoint hasn't shipped yet)
  // doesn't crash downstream Math.min/max/mean/toFixed. If every value
  // is missing the filtered array is empty and we take the empty-data
  // branch below — same visual treatment as `data=[]`.
  const cleanData = Array.isArray(data)
    ? data.filter((v) => typeof v === 'number' && Number.isFinite(v))
    : [];

  if (cleanData.length === 0) {
    // No data → render an empty SVG with the aria-label so the
    // surrounding tile still has its accessibility anchor.
    if (!ariaLabel) {
      // eslint-disable-next-line no-console
      console.warn('[Spark] ariaLabel is required for accessibility.');
    }
    return (
      <span className="jobe-spark jobe-spark--empty" ref={containerRef}>
        <svg
          className="jobe-spark__svg"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          role="img"
          aria-label={ariaLabel || ''}
        />
      </span>
    );
  }

  // Single-value series: synthesize a 7-point flat run so the spark
  // draws a clean horizontal line across the width rather than a
  // lone dot. Consumers passing one current reading (eval rate, etc.)
  // get a usable visual without having to fake a history client-side.
  // Multi-point series keep their actual shape.
  let renderData = cleanData;
  if (renderData.length === 1) {
    renderData = Array(7).fill(renderData[0]);
  } else if (renderData.length < 7 || renderData.length > 30) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Spark] usable data.length=${renderData.length} is outside the supported range [7, 30]; rendering anyway.`,
    );
  }
  if (!MODES.has(effectiveMode)) {
    // eslint-disable-next-line no-console
    console.warn(`[Spark] mode="${mode}" is invalid; defaulting to "absolute".`);
    effectiveMode = 'absolute';
  }
  if (!TONES.has(effectiveTone)) {
    // eslint-disable-next-line no-console
    console.warn(`[Spark] tone="${tone}" is invalid; defaulting to "ink".`);
    effectiveTone = 'ink';
  }
  if (effectiveTarget != null && effectiveMode === 'deviation') {
    // eslint-disable-next-line no-console
    console.warn('[Spark] target is ignored in deviation mode.');
    effectiveTarget = undefined;
  }
  if (!ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn('[Spark] ariaLabel is required for accessibility.');
  }

  // -------------------- y-range --------------------
  const mean = renderData.reduce((acc, v) => acc + v, 0) / renderData.length;
  let yMin;
  let yMax;
  if (effectiveMode === 'deviation') {
    const halfRange =
      Math.max(...renderData.map((v) => Math.abs(v - mean))) * 1.3 || 1;
    yMin = mean - halfRange;
    yMax = mean + halfRange;
  } else {
    yMin = Math.min(...renderData);
    yMax = Math.max(...renderData);
    if (effectiveTarget != null) {
      // Extend to include target with 5% padding on the extended edge.
      if (effectiveTarget < yMin) {
        const pad = (yMax - effectiveTarget) * 0.05;
        yMin = effectiveTarget - pad;
      } else if (effectiveTarget > yMax) {
        const pad = (effectiveTarget - yMin) * 0.05;
        yMax = effectiveTarget + pad;
      }
    }
    if (yMin === yMax) {
      // Flat series — avoid divide-by-zero; render as a centered line.
      yMin -= 0.5;
      yMax += 0.5;
    }
  }

  const yRange = yMax - yMin || 1;

  // -------------------- point projection --------------------
  const chartW = VB_W - PAD * 2;
  const chartH = VB_H - PAD * 2;
  const step = renderData.length > 1 ? chartW / (renderData.length - 1) : 0;

  const points = renderData.map((v, i) => {
    const x = PAD + step * i;
    // Invert y: SVG y grows down, our data grows up.
    const yNorm = (v - yMin) / yRange;
    const y = PAD + (1 - yNorm) * chartH;
    return { x, y, v };
  });

  const pathD = smoothPath(points);

  // Centerline (deviation) / target (absolute) y-coord.
  const centerlineY =
    effectiveMode === 'deviation'
      ? PAD + (1 - (mean - yMin) / yRange) * chartH
      : null;
  const targetY =
    effectiveMode === 'absolute' && effectiveTarget != null
      ? PAD + (1 - (effectiveTarget - yMin) / yRange) * chartH
      : null;

  // Hit-target rect width — 1/N of the chart, centered on each point.
  // Use the full viewBox width split into N slots so the first and last
  // points get an edge-anchored half-slot. This matches the "hover
  // anywhere near a point" expectation.
  const slot = VB_W / data.length;

  // -------------------- tooltip --------------------
  const showTooltip = hoverIdx != null && hoverIdx >= 0 && hoverIdx < data.length;
  const tipPct = showTooltip ? (points[hoverIdx].x / VB_W) * 100 : 0;
  const tipText = (i) => {
    const v = data[i];
    if (effectiveMode === 'deviation') {
      const delta = v - mean;
      return `${formatNum(v)}${unit} · ${fmtSigned(delta)}${unit} vs avg`;
    }
    return `${formatNum(v)}${unit}`;
  };

  // Tooltip top offset: pin to the chart top so it doesn't sit on
  // top of the data point. CSS rules pull it above with margin-top.
  return (
    <span
      className={`jobe-spark jobe-spark--tone-${effectiveTone}`}
      ref={containerRef}
      data-mode={effectiveMode}
    >
      <svg
        className="jobe-spark__svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel || ''}
      >
        {targetY != null && (
          <line
            className="jobe-spark__target"
            data-testid="spark-target"
            x1={PAD}
            x2={VB_W - PAD}
            y1={targetY}
            y2={targetY}
          />
        )}
        {centerlineY != null && (
          <line
            className="jobe-spark__centerline"
            data-testid="spark-centerline"
            x1={PAD}
            x2={VB_W - PAD}
            y1={centerlineY}
            y2={centerlineY}
          />
        )}
        <path className="jobe-spark__line" d={pathD} />
        {points.map((p, i) => {
          const isNow = i === points.length - 1;
          return (
            <circle
              key={`dot-${i}`}
              className={
                'jobe-spark__dot' +
                (isNow ? ' jobe-spark__dot--now' : '')
              }
              data-testid={isNow ? 'spark-dot-now' : 'spark-dot'}
              cx={p.x}
              cy={p.y}
              r={isNow ? 2.5 : 1.5}
            />
          );
        })}
        {points.map((p, i) => {
          const x = slot * i;
          return (
            <rect
              key={`hit-${i}`}
              className="jobe-spark__hit"
              data-testid="spark-hit"
              data-index={i}
              x={x}
              y={0}
              width={slot}
              height={VB_H}
              tabIndex={0}
              aria-describedby={`${reactId}-tip-${i}`}
              onPointerEnter={() => setHoverIdx(i)}
              onPointerMove={() => setHoverIdx(i)}
              onPointerLeave={() => setHoverIdx(null)}
              onFocus={() => setHoverIdx(i)}
              onBlur={() => setHoverIdx(null)}
            >
              <title id={`${reactId}-tip-${i}`}>{tipText(i)}</title>
            </rect>
          );
        })}
      </svg>
      {showTooltip && (
        <div
          className="jobe-spark__tooltip"
          data-testid="spark-tooltip"
          role="tooltip"
          style={{ left: `${tipPct}%`, top: 0 }}
        >
          {tipText(hoverIdx)}
        </div>
      )}
    </span>
  );
}

export default Spark;
